/**
 * 상단 1depth 메뉴 활성 인덱스: 현재 경로(URL)로 먼저 찾고, 없으면 제목배너 breadcrumb으로 추론.
 * @param {Array<{ href?: string, label?: string, children?: unknown[] }>} menuData
 * @param {string} pathname location.pathname (쿼리 제외)
 * @param {Array<{ href?: string, label?: string }>|null|undefined} breadcrumbPath
 * @returns {number|null}
 */
export function resolveActiveTopMenuIndex(menuData, pathname, breadcrumbPath) {
  const byPath = findActiveTopMenuIndexByPath(menuData, pathname);
  if (byPath !== null) return byPath;
  return findActiveTopMenuIndexByBreadcrumb(menuData, pathname, breadcrumbPath);
}

/** 메뉴 트리에서 pathname과 일치하는 노드가 속한 최상위 인덱스 */
export function findActiveTopMenuIndexByPath(menuData, pathname) {
  if (!menuData || !Array.isArray(menuData) || menuData.length === 0 || !pathname) {
    return null;
  }
  const walk = (nodes, currentPath, topIndex = null) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const currentTop = topIndex !== null ? topIndex : i;
      const h = node.href && node.href !== '#' ? String(node.href).split('?')[0] : '';
      if (h && h === currentPath) return currentTop;
      if (node.children?.length) {
        const found = walk(node.children, currentPath, currentTop);
        if (found !== null) return found;
      }
    }
    return null;
  };
  return walk(menuData, pathname);
}

function normalizePathHref(href) {
  if (!href || href === '#') return '';
  const s = String(href).trim();
  if (/^(https?:)?\/\//i.test(s)) return '';
  return s.split('?')[0] || '';
}

const HOME_LABELS = new Set(['', 'home', '홈']);

/**
 * 경로로는 메뉴에 없을 때: 브레드크럼의 href(역순 우선)·라벨로 1depth 추론
 */
export function findActiveTopMenuIndexByBreadcrumb(menuData, pathname, breadcrumbPath) {
  if (!menuData?.length || !breadcrumbPath?.length) return null;

  const crumbs = breadcrumbPath.filter((c) => c && (c.label != null || c.href != null));

  // 1) 브레드크럼에 나온 href들 — 뒤쪽(구체적)부터 메뉴 트리와 매칭
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const h = normalizePathHref(crumbs[i]?.href);
    if (!h || h === '/') continue;
    const idx = findActiveTopMenuIndexByPath(menuData, h);
    if (idx !== null) return idx;
  }

  // 2) 현재 pathname이 어떤 크럼 href의 하위 경로인지 (메뉴에 없는 상세 URL)
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const h = normalizePathHref(crumbs[i]?.href);
    if (!h || h === '/' || h === pathname) continue;
    if (pathname.startsWith(h.endsWith('/') ? h : `${h}/`)) {
      const idx = findActiveTopMenuIndexByPath(menuData, h);
      if (idx !== null) return idx;
    }
  }

  // 3) 첫 번째 «홈이 아닌» 크럼 라벨 ↔ 1depth 메뉴 라벨 (정확 일치 → 포함)
  for (const c of crumbs) {
    const raw = c?.label != null ? String(c.label).trim() : '';
    const lab = raw.toLowerCase();
    if (!lab || HOME_LABELS.has(lab)) continue;

    let exact = null;
    let fuzzy = null;
    for (let mi = 0; mi < menuData.length; mi++) {
      const ml = menuData[mi]?.label != null ? String(menuData[mi].label).trim().toLowerCase() : '';
      if (!ml) continue;
      if (ml === lab) {
        exact = mi;
        break;
      }
      if (ml.includes(lab) || lab.includes(ml)) {
        if (fuzzy === null) fuzzy = mi;
      }
    }
    if (exact !== null) return exact;
    if (fuzzy !== null) return fuzzy;
    break;
  }

  return null;
}
